import React, { useState, useEffect} from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';

import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon, 
  CreditCardIcon,
  StarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { bookingsAPI } from '../utils/api';
import { statusColor, statusLabel, formatDate, formatBudget, isClosed } from '../utils/bookings';
import { useAuth } from '../context/AuthContext';

const DashboardClient = () => {
  const { user, loading, isSmart } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [stats] = useState({
    totalBookings: 12,
    completedServices: 9,
    totalSpent: 125000,
    rating: 4.8
  });

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login');
      } else if (isSmart) {
        navigate('/smart/dashboard');
      }
    }
    bookingsAPI.getAll().then(res => setBookings(res.data || []));
  }, [user, loading, isSmart, navigate]);

  const tabs = [
    { id: 'upcoming', name: 'À venir', count: bookings.filter(b => !isClosed(b.statut)).length },
    { id: 'past', name: 'Passées', count: bookings.filter(b => isClosed(b.statut)).length },
    { id: 'all', name: 'Toutes', count: bookings.length },
  ];

  const statCards = [
    { label: 'Réservations totales', value: stats.totalBookings, icon: CalendarIcon, color: '#F97316' },
    { label: 'Services complétés', value: stats.completedServices, icon: StarIcon, color: '#10B981' },
    { label: 'Total dépensé', value: `${stats.totalSpent.toLocaleString()} FCFA`, icon: CreditCardIcon, color: '#3B82F6' },
    { label: 'Note moyenne', value: `${stats.rating} ★`, icon: StarIcon, color: '#F59E0B' },
  ];


  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header avec dégradé */}
      <div className="relative bg-[#F97316] pt-12 pb-10 mb-10">      
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl lg:text-4xl font-display font-bold text-white">
                  Bonjour, {user?.nom || 'Client'} 
                </h1>
                <p className="text-white/80 mt-1">Bienvenue sur votre espace client</p>
              </div>
              <Link to="/services" className="bg-white/40 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/30 transition-all">
                + Nouvelle réservation
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container-custom -mt-15 pb-12">
        

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-[#F97316] text-white shadow-lg shadow-[#F97316]/25'
                  : 'bg-[#111111] text-[#AAAAAA] hover:bg-[#1a1a1a] border border-[#252525]'
              }`}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-[#252525]'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Réservations */}
        <div className="space-y-4">
          {bookings.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#111111] rounded-2xl border border-[#252525] text-center py-16"
            >
              <div className="w-20 h-20 bg-[#F97316]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="h-10 w-10 text-[#F97316]" />
              </div>
              <p className="text-[#AAAAAA] text-lg mb-4">Aucune réservation pour le moment</p>
              <Link to="/services" className="inline-block bg-[#F97316] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#e8630a] transition-all">
                Trouver un service
              </Link>
            </motion.div>
          ) : (
            bookings.map((booking, index) => (
              <motion.div
                key={booking._id || index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#111111] rounded-2xl border border-[#252525] p-5 hover:border-[#F97316]/30 transition-all"
              >
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-white text-lg">
                        {booking.description || 'Demande'}
                      </h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor(booking.statut)}`}>
                        {statusLabel(booking.statut)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-[#AAAAAA]">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        {formatDate(booking.dateIntervention)}
                      </span>
                      <span className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4" />
                        {booking.urgence || 'Normal'}
                      </span>
                      <span className="flex items-center gap-1">
                      <MapPinIcon className="h-4 w-4" />
                      {booking.adresse || 'Adresse à définir'}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#F97316] to-[#e8630a] rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {booking.smartName?.[0] || 'S'}
                      </div>
                      <span className="text-white text-sm">
                        {booking.smartName || 'Smart à assigner'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#F97316]">
                      {formatBudget(booking.montantPaye || booking.budget)}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button className="px-4 py-2 rounded-xl bg-[#1a1a1a] text-white text-sm hover:bg-[#252525] transition">
                        Détails
                      </button>
                      {booking.statut === 'en_attente' && (
                        <button className="px-4 py-2 rounded-xl bg-red-500/10 text-red-500 text-sm hover:bg-red-500/20 transition">
                          Annuler
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Section aide */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-8 p-5 bg-gradient-to-r from-[#111111] to-[#0a0a0a] rounded-2xl border border-[#252525] flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#F97316]/10 rounded-xl flex items-center justify-center">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-[#F97316]" />
            </div>
            <div>
              <p className="text-white font-semibold">Besoin d'aide ?</p>
              <p className="text-sm text-[#AAAAAA]">Notre support est disponible 7j/7</p>
            </div>
          </div>
          <Link to="/contact" className="text-[#F97316] font-semibold hover:underline">
            Contacter le support →
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardClient;